/**
 * Admin Dealers List Page
 * Phase 2E: Master Admin UI
 * 
 * Features:
 * - Dealer list table with search/filter
 * - Enable/disable dealers
 * - Create new dealer
 * - View dealer details
 */
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { Download, MoreHorizontal, Eye, ArrowLeftRight, ToggleLeft, Archive as ArchiveIcon, ArchiveRestore } from "lucide-react";

const AdminDealersPage = ({ onNavigate }) => {
  const { apiFetch, user } = useAuth();
  const { success: showToast, error: showError } = useToast();
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [total, setTotal] = useState(0);
  const [fetchTick, setFetchTick] = useState(0); // increment to re-trigger fetch

  // iter66 — server-side pagination state. Default page size 25 per spec.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);

  // Reassign Reseller modal state
  const [reassignDealer, setReassignDealer] = useState(null); // {id, name, current_reseller_id}
  const [reassignTarget, setReassignTarget] = useState("");   // "" = unset, "__none__" = Direct, or reseller id
  const [reassignSaving, setReassignSaving] = useState(false);
  const [resellerOptions, setResellerOptions] = useState([]); // [{id, name, is_active}]
  const [exporting, setExporting] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null); // dealer object
  const [archiving, setArchiving] = useState(false);

  // Bulk archive state — only used when statusFilter === 'disabled'.
  // selectedIds holds dealer IDs the master admin has ticked.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkArchiving, setBulkArchiving] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      // Use apiFetch to inherit auth headers; request blob response. Archived
      // dealers are excluded unless Show Archived toggle is on.
      const url = showArchived ? "/admin/dealers/export?include_archived=true" : "/admin/dealers/export";
      const res = await apiFetch(url, { method: "GET", responseType: "blob" });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "text/csv" });
      const dlUrl = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `verified-demand-dealers-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
      showToast("CSV downloaded");
    } catch (e) {
      showError(e.response?.data?.detail || "Failed to export dealers");
    } finally {
      setExporting(false);
    }
  };

  const handleConfirmArchive = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      await apiFetch(`/admin/dealers/${archiveTarget.id}/archive`, { method: "POST" });
      showToast(`Dealer '${archiveTarget.name}' archived`);
      setArchiveTarget(null);
      setFetchTick(t => t + 1);
    } catch (e) {
      showError(e.response?.data?.detail || "Failed to archive dealer");
    } finally {
      setArchiving(false);
    }
  };

  const handleArchiveClick = (dealer) => {
    // Enforce client-side too: block when active and show the same message.
    if (dealer.is_active) {
      showError("You must disable this dealer before archiving them.");
      return;
    }
    setArchiveTarget(dealer);
  };

  const handleUnarchive = async (dealer) => {
    try {
      await apiFetch(`/admin/dealers/${dealer.id}/archive?archived=false`, { method: "POST" });
      showToast(`Dealer '${dealer.name}' unarchived`);
      setFetchTick(t => t + 1);
    } catch (e) {
      showError(e.response?.data?.detail || "Failed to unarchive dealer");
    }
  };

  // Bulk archive helpers ---------------------------------------------------
  const toggleSelectOne = (dealerId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(dealerId)) next.delete(dealerId);
      else next.add(dealerId);
      return next;
    });
  };

  const toggleSelectAll = (eligibleIds, allSelected) => {
    setSelectedIds(allSelected ? new Set() : new Set(eligibleIds));
  };

  const handleConfirmBulkArchive = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkArchiving(true);
    try {
      const res = await apiFetch("/admin/dealers/bulk-archive", {
        method: "POST",
        data: { dealer_ids: ids },
      });
      const archived = res.data?.archived_count ?? ids.length;
      const skipped = (res.data?.skipped_active || []).length;
      const already = (res.data?.already_archived || []).length;
      let msg = `Archived ${archived} dealer${archived === 1 ? "" : "s"}`;
      if (skipped) msg += ` · ${skipped} skipped (still active)`;
      if (already) msg += ` · ${already} already archived`;
      showToast(msg);
      setBulkConfirmOpen(false);
      setSelectedIds(new Set());
      setFetchTick(t => t + 1);
    } catch (e) {
      showError(e.response?.data?.detail || "Failed to archive dealers");
    } finally {
      setBulkArchiving(false);
    }
  };

  // Fetch active resellers the first time a reassign modal is opened
  const loadResellers = async () => {
    if (resellerOptions.length > 0) return;
    try {
      const res = await apiFetch("/admin/resellers");
      const list = (res.data?.resellers || []).filter(r => r.is_active);
      setResellerOptions(list);
    } catch (e) {
      console.error("Failed to load resellers", e);
      setResellerOptions([]);
    }
  };

  const openReassignModal = async (dealer) => {
    await loadResellers();
    setReassignTarget(dealer.reseller_id || "__none__");
    setReassignDealer(dealer);
  };

  const handleConfirmReassign = async () => {
    if (!reassignDealer || !reassignTarget) return;
    setReassignSaving(true);
    try {
      const reseller_id = reassignTarget === "__none__" ? null : reassignTarget;
      const res = await apiFetch(`/admin/dealers/${reassignDealer.id}/reseller`, {
        method: "PATCH",
        data: { reseller_id },
      });
      // Optimistic inline update — patch only the affected row in local state.
      // No full table re-fetch, no skeleton flicker. The DB write is already
      // persisted, so local state is the source of truth until the next
      // natural reload (page change, search, manual refresh).
      const newResellerName = reseller_id
        ? (resellerOptions.find(r => r.id === reseller_id)?.name || null)
        : null;
      setDealers(prev => prev.map(d =>
        d.id === reassignDealer.id
          ? { ...d, reseller_id, reseller_name: newResellerName }
          : d
      ));
      showToast(res.data?.message || "Dealer reassigned");
      setReassignDealer(null);
      setReassignTarget("");
    } catch (e) {
      showError(e.response?.data?.detail || "Failed to reassign dealer");
    } finally {
      setReassignSaving(false);
    }
  };

  // Imperative fetch function — called directly, not via useEffect deps chain
  const runFetch = async (searchVal, pageOverride, limitOverride) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchVal) params.append("search", searchVal);
      // iter66 — server-side pagination per spec.
      const usePage = pageOverride !== undefined ? pageOverride : page;
      const useLimit = limitOverride !== undefined ? limitOverride : pageSize;
      params.append("page", String(usePage));
      params.append("limit", String(useLimit));
      if (showArchived) params.append("include_archived", "true");
      const response = await apiFetch(`/admin/dealers?${params.toString()}`);
      const rawDealers = response.data.dealers || [];
      const filtered = rawDealers.filter(d => {
        const t = d.account_type || 'dealer';
        if (t !== 'dealer') { console.warn(`[DEALERS] skipping account_type=${t}`); return false; }
        return true;
      });
      setDealers(filtered);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
      // If the API echoed back a normalized page (e.g. legacy offset → page 1),
      // mirror it so the UI controls stay in sync.
      if (response.data.page) setPage(response.data.page);
    } catch (err) {
      console.error("Failed to load dealers:", err);
    } finally {
      setLoading(false);
    }
  };

  // fetchDealers for Refresh button — explicitly retains current page
  const fetchDealers = () => runFetch(search, page, pageSize);

  // Load on mount and when archived toggle changes.
  // statusFilter is intentionally NOT a dependency: filtering is done
  // client-side over the already-fetched list, so changing the dropdown
  // does not require a new round-trip — the table re-filters instantly
  // and never shows an empty/skeleton state on filter change.
  // The eslint-disable below is INTENTIONAL: runFetch is defined inline
  // (not memoized) so a new function reference is created on every render;
  // adding it to the deps array would cause an infinite re-fetch loop. The
  // function does close over `search`/`apiFetch` correctly because each
  // render's runFetch reads the latest closure values when invoked. Audit
  // Feb 27 2026 — verified safe to keep this opt-out.
  useEffect(() => {
    runFetch(search, page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTick, showArchived, page, pageSize]);

  // Whenever search, archived-toggle, or status-filter changes, reset to
  // page 1 so the user doesn't land on an empty late page.
  useEffect(() => {
    setPage(1);
  }, [search, showArchived, statusFilter]);

  // Reset bulk selection whenever the visible row set changes (status
  // filter, archived toggle, search, or after a refetch). Selecting rows
  // that are no longer in view would be a footgun for bulk archive.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [statusFilter, showArchived, search, fetchTick]);

  const handleToggleStatus = async (dealer) => {
    try {
      await apiFetch(`/admin/dealers/${dealer.id}`, {
        method: "PUT",
        data: { is_active: !dealer.is_active }
      });
      showToast(`Dealer ${dealer.is_active ? "disabled" : "enabled"} successfully`);
      fetchDealers();
    } catch (_err) {
      showError("Failed to update dealer status");
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    showToast(`${label} copied to clipboard`);
  };

  const filteredDealers = dealers.filter(d => {
    if (statusFilter === "active") return d.is_active;
    if (statusFilter === "disabled") return !d.is_active;
    return true;
  });

  // Bulk archive is only available when the user has narrowed the view
  // to "Disabled" — active dealers cannot be archived in bulk per spec.
  const bulkMode = statusFilter === "disabled";
  const eligibleBulkIds = bulkMode
    ? filteredDealers.filter(d => !d.is_active && !d.archived).map(d => d.id)
    : [];
  const allEligibleSelected =
    eligibleBulkIds.length > 0 &&
    eligibleBulkIds.every(id => selectedIds.has(id));
  const someEligibleSelected =
    eligibleBulkIds.some(id => selectedIds.has(id));
  const selectedCount = bulkMode
    ? eligibleBulkIds.filter(id => selectedIds.has(id)).length
    : 0;

  // RBAC check
  if (user?.role !== "MASTER_ADMIN") {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="py-16 text-center">
            <div className="text-6xl mb-4">🚫</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You don&apos;t have permission to access the Admin area.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dealer Management</h1>
          <p className="text-gray-600 mt-1">{total} dealers registered</p>
          <p className="text-xs text-gray-400 mt-0.5">Showing: Dealers only</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => onNavigate("admin-resellers")} 
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            data-testid="manage-resellers-btn"
          >
            <span className="mr-2">🤝</span> Manage Resellers
          </Button>
          <Button onClick={() => onNavigate("admin-dealer-new")} className="bg-blue-600 hover:bg-blue-700">
            <span className="mr-2">+</span> Create Dealer
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <Input
                placeholder="Search by name or public key..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-md"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchDealers}>Refresh</Button>
            <div className="flex items-center gap-2 ml-2 border-l border-gray-200 pl-3">
              <Switch
                id="show-archived-toggle"
                checked={showArchived}
                onCheckedChange={setShowArchived}
                data-testid="show-archived-toggle"
              />
              <label htmlFor="show-archived-toggle" className="text-sm text-gray-600 select-none cursor-pointer">
                Show Archived
              </label>
            </div>
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={exporting}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="export-dealers-csv-btn"
            >
              <Download size={14} className="mr-1.5" />
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bulk action bar — visible only when status filter is 'Disabled'
          and at least one disabled dealer has been ticked. */}
      {bulkMode && selectedCount > 0 && (
        <div
          className="mb-4 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3"
          data-testid="bulk-archive-action-bar"
        >
          <div className="text-sm text-amber-900">
            <span className="font-semibold" data-testid="bulk-archive-selected-count">
              {selectedCount}
            </span>{" "}
            disabled dealer{selectedCount === 1 ? "" : "s"} selected
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
              data-testid="bulk-archive-clear-btn"
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={() => setBulkConfirmOpen(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="bulk-archive-trigger-btn"
            >
              <ArchiveIcon size={14} className="mr-1.5" />
              Archive Selected
            </Button>
          </div>
        </div>
      )}

      {/* Dealers Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                  <Skeleton className="h-8 w-20" />
                </div>
              ))}
            </div>
          ) : filteredDealers.length === 0 ? (
            <div className="py-16 text-center">
              <div className="text-4xl mb-4">🏪</div>
              <h3 className="text-lg font-medium text-gray-900">No dealers found</h3>
              <p className="text-gray-500 mt-1">Try adjusting your search or filters</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {bulkMode && (
                    <th className="w-10 px-4 py-3" data-testid="bulk-select-header">
                      <Checkbox
                        checked={
                          allEligibleSelected
                            ? true
                            : someEligibleSelected
                            ? "indeterminate"
                            : false
                        }
                        onCheckedChange={() =>
                          toggleSelectAll(eligibleBulkIds, allEligibleSelected)
                        }
                        disabled={eligibleBulkIds.length === 0}
                        aria-label="Select all disabled dealers"
                        data-testid="bulk-select-all-checkbox"
                      />
                    </th>
                  )}
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Dealer</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Franchise</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Public Key</th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Leads</th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredDealers.map((dealer) => (
                  <tr key={dealer.id} className={`hover:bg-gray-50 ${dealer.archived ? "bg-gray-100 opacity-60 italic" : !dealer.is_active ? "bg-gray-50 opacity-75" : ""}`} data-testid={`dealer-row-${dealer.id}`}>
                    {bulkMode && (
                      <td className="px-4 py-4 align-middle">
                        {!dealer.is_active && !dealer.archived ? (
                          <Checkbox
                            checked={selectedIds.has(dealer.id)}
                            onCheckedChange={() => toggleSelectOne(dealer.id)}
                            aria-label={`Select ${dealer.name}`}
                            data-testid={`bulk-select-checkbox-${dealer.id}`}
                          />
                        ) : null}
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                          {dealer.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{dealer.name}</div>
                          <div className="text-xs text-gray-500">
                            {dealer.created_at ? new Date(dealer.created_at).toLocaleDateString() : "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-testid={`dealer-franchise-${dealer.id}`}>
                      {dealer.franchise_make ? (
                        <span className="text-sm text-gray-700">{dealer.franchise_make}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full text-[11px] font-semibold" data-testid={`dealer-franchise-missing-${dealer.id}`}>
                          ⚠ Not Set
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                          {dealer.public_key?.substring(0, 12)}...
                        </code>
                        <button
                          onClick={() => copyToClipboard(dealer.public_key, "Public key")}
                          className="text-gray-400 hover:text-gray-600 text-sm"
                          title="Copy public key"
                        >
                          📋
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-medium text-gray-900">{dealer.user_count || 0}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-medium text-gray-900">{dealer.lead_count || 0}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Badge variant={dealer.is_active ? "default" : "secondary"} className={dealer.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}>
                        {dealer.is_active ? "Active" : "Disabled"}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-2"
                            data-testid={`dealer-actions-btn-${dealer.id}`}
                            aria-label="Dealer actions"
                          >
                            <MoreHorizontal size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52" data-testid={`dealer-actions-menu-${dealer.id}`}>
                          <DropdownMenuItem
                            onClick={() => onNavigate("admin-dealer-detail", dealer.id)}
                            data-testid={`action-view-${dealer.id}`}
                          >
                            <Eye size={14} className="mr-2 text-gray-500" /> View
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => openReassignModal(dealer)}
                            data-testid={`action-reassign-${dealer.id}`}
                          >
                            <ArrowLeftRight size={14} className="mr-2 text-gray-500" /> Reassign Reseller
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleToggleStatus(dealer)}
                            data-testid={`action-toggle-${dealer.id}`}
                          >
                            <ToggleLeft size={14} className={`mr-2 ${dealer.is_active ? "text-red-500" : "text-emerald-500"}`} />
                            {dealer.is_active ? "Disable" : "Enable"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {dealer.archived ? (
                            <DropdownMenuItem
                              onClick={() => handleUnarchive(dealer)}
                              data-testid={`action-unarchive-${dealer.id}`}
                            >
                              <ArchiveRestore size={14} className="mr-2 text-emerald-600" /> Unarchive Dealer
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => handleArchiveClick(dealer)}
                              className="text-amber-700 focus:text-amber-800"
                              data-testid={`action-archive-${dealer.id}`}
                            >
                              <ArchiveIcon size={14} className="mr-2" /> Archive Dealer
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* iter66 — server-side pagination controls. Hidden when there is
              nothing to paginate (≤ one page of results). */}
          {!loading && totalPages > 1 && (
            <div
              className="flex items-center justify-between px-4 py-3 border-t border-gray-200"
              data-testid="dealers-pagination"
            >
              <div className="text-sm text-gray-600" data-testid="dealers-pagination-info">
                Showing{" "}
                <span className="font-semibold">
                  {total === 0 ? 0 : (page - 1) * pageSize + 1}
                </span>
                {" – "}
                <span className="font-semibold">
                  {Math.min(page * pageSize, total)}
                </span>{" "}
                of <span className="font-semibold">{total}</span> dealers
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={pageSize}
                  onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="dealers-page-size-select"
                  aria-label="Page size"
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>{n} / page</option>
                  ))}
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                  data-testid="dealers-prev-page-btn"
                >
                  Previous
                </Button>
                <span
                  className="text-sm text-gray-700 px-2"
                  data-testid="dealers-page-indicator"
                >
                  Page <span className="font-semibold">{page}</span> of{" "}
                  <span className="font-semibold">{totalPages}</span>
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                  data-testid="dealers-next-page-btn"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reassign Reseller Modal */}
      <Dialog open={!!reassignDealer} onOpenChange={(open) => { if (!open) { setReassignDealer(null); setReassignTarget(""); } }}>
        <DialogContent className="sm:max-w-[460px]" data-testid="reassign-reseller-modal">
          <DialogHeader>
            <DialogTitle>Reassign Reseller</DialogTitle>
            <DialogDescription>
              Change which reseller manages <span className="font-semibold">{reassignDealer?.name}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
              Assign to
            </label>
            <Select value={reassignTarget} onValueChange={setReassignTarget}>
              <SelectTrigger data-testid="reassign-reseller-select">
                <SelectValue placeholder="Select a reseller..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" data-testid="reassign-option-none">
                  No Reseller (Direct)
                </SelectItem>
                {resellerOptions.map((r) => (
                  <SelectItem key={r.id} value={r.id} data-testid={`reassign-option-${r.id}`}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {resellerOptions.length === 0 && (
              <p className="text-xs text-gray-500 mt-2">
                No active resellers available. You can still set this dealer to Direct.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setReassignDealer(null); setReassignTarget(""); }}
              disabled={reassignSaving}
              data-testid="reassign-reseller-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={handleConfirmReassign}
              disabled={reassignSaving || !reassignTarget}
              data-testid="reassign-reseller-confirm"
            >
              {reassignSaving ? "Saving..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!archiveTarget} onOpenChange={(open) => { if (!open) setArchiveTarget(null); }}>
        <DialogContent className="sm:max-w-[440px]" data-testid="archive-dealer-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <ArchiveIcon size={18} /> Archive Dealer
            </DialogTitle>
            <DialogDescription className="pt-2 text-gray-700">
              Are you sure you want to archive <span className="font-semibold">{archiveTarget?.name}</span>? They will be hidden from the dealer list. You can restore them later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setArchiveTarget(null)}
              disabled={archiving}
              data-testid="archive-dealer-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmArchive}
              disabled={archiving}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="archive-dealer-confirm"
            >
              {archiving ? "Archiving…" : "Confirm Archive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Archive Confirmation Modal */}
      <Dialog open={bulkConfirmOpen} onOpenChange={(open) => { if (!open) setBulkConfirmOpen(false); }}>
        <DialogContent className="sm:max-w-[460px]" data-testid="bulk-archive-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <ArchiveIcon size={18} /> Archive Selected Dealers
            </DialogTitle>
            <DialogDescription className="pt-2 text-gray-700">
              Archive{" "}
              <span className="font-semibold" data-testid="bulk-archive-modal-count">
                {selectedCount}
              </span>{" "}
              disabled dealer{selectedCount === 1 ? "" : "s"}? They will be hidden
              from the dealer list but all data will be preserved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setBulkConfirmOpen(false)}
              disabled={bulkArchiving}
              data-testid="bulk-archive-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmBulkArchive}
              disabled={bulkArchiving || selectedCount === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="bulk-archive-confirm-btn"
            >
              {bulkArchiving ? "Archiving…" : `Archive ${selectedCount} Dealer${selectedCount === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDealersPage;
